import React, { useEffect, useState, useContext, useCallback } from "react";
import axios from "axios";
import { useParams, Link, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "../styles/ClientProfile.css";

const statusLabels = {
  1: "Probleme (Cu Revenire)",
  2: "Activ",
  3: "Montaj finalizat",
  4: "Dosar Prosumator",
  5: "Certificat de Racordare",
};

const ClientProfile = () => {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("informatii");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [panelSeries, setPanelSeries] = useState([]);
  const [newPanel, setNewPanel] = useState("");
  const [prosumatorLogs, setProsumatorLogs] = useState([]);
  const [generalLogs, setGeneralLogs] = useState([]);
  const [newProsumatorLog, setNewProsumatorLog] = useState("");
  const [newGeneralLog, setNewGeneralLog] = useState("");
  const [showProsumatorModal, setShowProsumatorModal] = useState(false);
  const [showGeneralLogsModal, setShowGeneralLogsModal] = useState(false);
  const [lastStatusUpdate, setLastStatusUpdate] = useState("");
  const [lastUpdatedBy, setLastUpdatedBy] = useState("");
  const [documents, setDocuments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState("priza_pamant");
  const [lastPanelAddTime, setLastPanelAddTime] = useState(0);
  const [lastUploadTime, setLastUploadTime] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [acumulatorSeries, setAcumulatorSeries] = useState([]);
  const [newAcumulator, setNewAcumulator] = useState("");
  const [lastAcumulatorAddTime, setLastAcumulatorAddTime] = useState(0);
  const [invertorSeries, setInvertorSeries] = useState([]); 
  const [newInvertor, setNewInvertor] = useState(""); 
  const [lastInvertorAddTime, setLastInvertorAddTime] = useState(0); 
  const [smartMeterSeries, setSmartMeterSeries] = useState([]);
  const [newSmartMeter, setNewSmartMeter] = useState("");
  const [lastSmartMeterAddTime, setLastSmartMeterAddTime] = useState(0);

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3; // 📌 Număr de serii afișate pe pagină

  //  Calculează numărul total de pagini
  const totalPages = Math.ceil(panelSeries.length / itemsPerPage);

  //  Determină ce serii sunt afișate pe pagina curentă
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = panelSeries.slice(indexOfFirstItem, indexOfLastItem);

  const fetchDocuments = useCallback(() => {
    axios
      .get(`https://casaverde.marenergy.ro/api/get_documents.php?client_id=${id}`)
      .then((response) => {
        if (Array.isArray(response.data)) {
          setDocuments(response.data);
        } else {
          setDocuments([]); // Setează un array gol în loc de `null` sau `undefined`
        }
      })
      .catch(() => {
        console.error("Eroare la incarcarea imaginilor");
        setDocuments([]); // Dacă apare eroare, evităm crash-ul
      });
  }, [id]);

  useEffect(() => {
    if(!user) {
      navigate("/login");
      return;
    }

    axios
      .get(`https://casaverde.marenergy.ro/api.php?action=get_client&id=${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })

      .then((response) => {
        if (response.data && response.data.id) {
          setClient(response.data);
          setSelectedStatus(response.data.status);
          setPanelSeries(response.data.panels || []);
          setAcumulatorSeries(response.data.acumulator_series || []);
          setInvertorSeries(response.data.invertor_series || []);
          setSmartMeterSeries(response.data.smart_meter_series || []);
          setProsumatorLogs(response.data.prosumator_logs || []);
          setGeneralLogs(response.data.general_logs || []);
          setLastStatusUpdate(response.data.last_status_update || "Nicio actualizare");
          setLastUpdatedBy(response.data.last_updated_by_name || "Necunoscut");
        } else {
          setError("Clientul nu a fost găsit");
        }
      })
      .catch(() => setError("Eroare la obținerea datelor despre client"));

    axios
      .get(`https://casaverde.marenergy.ro/api.php?action=get_prosumator_logs&client_id=${id}`)
      .then((response) => setProsumatorLogs(response.data))
      .catch(() => console.error("Eroare la încărcarea istoricului prosumatorului"));

    axios
      .get(`https://casaverde.marenergy.ro/api.php?action=get_general_logs&client_id=${id}`)
      .then((response) => setGeneralLogs(response.data))
      .catch(() => console.error("Eroare la încărcarea mesajelor generale"));

    fetchDocuments();
    setSelectedDocumentType("priza_pamant"); 
  }, [id, user, navigate, fetchDocuments]);

  const handleChangeStatus = () => {
    if (!user || !user.user_id) {
      console.error("❌ Eroare: user_id lipsă");
      return;
    }

    if (selectedStatus === client?.status) {
      alert("⚠️ Statusul este deja setat la aceasta valoare. Va rugam sa alegeti un status diferit.");
      return;
    }
    
    if (panelSeries.length < 5) {
      alert("⚠️ Trebuie sa adaugi seriile panourilor fotovoltaice inainte sa schimbi statusul clientului!");
      return;
    }

    if (acumulatorSeries.length === 0) {
      alert("⚠️ Trebuie sa adaugati cel putin o serie de acumulatori inainte de a schimba statusul clientului!");
      return;
    }

    if (invertorSeries.length === 0) {
      alert("⚠️ Trebuie sa adaugati cel putin o serie de invertor inainte de a schimba statusul clientului!");
      return;
    }    

    const hasGroundingPhoto = documents.some(doc => doc.document_type === "priza_pamant");
    if (!hasGroundingPhoto) {
      alert("Trebuie sa incarcati o poza cu priza de pamant inainte de a schimba statusul clientului!");
      return;
    }

    if (selectedStatus === "5") {
      const hasCertificate = documents.some(doc => doc.document_type === "certificat_racordare");
      if (!hasCertificate) {
        alert("Trebuie sa incarcati certificatul de racordare pentru a putea seta statusul la 'Certificat de Racordare'!");
        return;
      }
    }

    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "update_status",
        client_id: id,
        status: selectedStatus,
        user_id: user.user_id,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((response) => {
        alert(response.data.message);

        setClient((prevClient) => ({
          ...prevClient,
          status: selectedStatus
        })); 

        const currentTime = new Date().toLocaleString();
        setLastStatusUpdate(currentTime);
        setLastUpdatedBy(user.name);
      })
      .catch(() => alert("Eroare la actualizarea statusului"));
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files[0]);
  };

  const handleUpload = () => {
    if (!selectedFile) {
      alert("Selectati un fisier!");
      return;
    }
  
    // Definim extensiile permise pentru fiecare tip de document
    const allowedImageExtensions = ["jpg", "jpeg", "png"];
    const allowedCertExtensions = ["pdf"];
    const fileExtension = selectedFile.name.split(".").pop().toLowerCase();
  
    if (selectedDocumentType === "priza_pamant" && !allowedImageExtensions.includes(fileExtension)) {
      alert("Tip de fisier invalid! Pentru Poza Priza sunt permise doar JPG, JPEG si PNG.");
      return;
    }
  
    if (selectedDocumentType === "certificat_racordare" && !allowedCertExtensions.includes(fileExtension)) {
      alert("Tip de fisier invalid! Certificatul de Racordare trebuie sa fie in format PDF.");
      return;
    }
  
    if (selectedFile.size > 10 * 1024 * 1024) {
      alert("Fisierul este prea mare! (maxim 10MB)");
      return;
    }
  
    // 🚨 Verificăm dacă există deja un certificat de racordare pentru acest client
    if (selectedDocumentType === "certificat_racordare" && documents.some(doc => doc.document_type === "certificat_racordare")) {
      alert("Exista deja un Certificat de Racordare incarcat pentru acest client!");
      return;
    }
  
    const currentTime = Date.now();
    if (currentTime - lastUploadTime < 10000) { // Restricție de 10 secunde
      alert("Asteptati 10 secunde inainte de a incarca alt fisier!");
      return;
    }
  
    setIsUploading(true);
  
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("client_id", id);
    formData.append("document_type", selectedDocumentType); // Trimitem tipul documentului
  
    axios
      .post("https://casaverde.marenergy.ro/api/upload.php", formData, {
        headers: { "Content-Type": "multipart/form-data", Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((response) => {
        alert(response.data.message);
        setSelectedFile(null);
        setLastUploadTime(currentTime);
        fetchDocuments(); // Reîmprospătăm lista de documente
        setIsUploading(false);
      })
      .catch((error) => {
        console.error("Eroare la incarcare:", error);
        alert(error.response?.data?.message || "Eroare la incarcare");
        setIsUploading(false);
      });
  };  

  const handleDownloadExcel = () => {
    if (!client) {
      alert("Clientul nu este definit.");
      return;
    }
  
    if (
      panelSeries.length === 0 &&
      acumulatorSeries.length === 0 &&
      invertorSeries.length === 0 &&
      smartMeterSeries.length === 0
    ) {
      alert("Nu exista serii pentru a fi descarcate.");
      return;
    }
  
    // Generalized mapping function
    const buildDataWithType = (seriesArray, type, key) =>
      seriesArray.map((item) => ({
        TipSerie: type,
        Serie: item[key],
      }));
  
    const allSeriesData = [
      ...buildDataWithType(panelSeries, "Panou", "series"),
      ...buildDataWithType(acumulatorSeries, "Acumulator", "series"),
      ...buildDataWithType(invertorSeries, "Invertor", "series_invertor"),
      ...buildDataWithType(smartMeterSeries, "SmartMeter", "series_smart_meter"),
    ].map((item, index) => ({
      Nr: index + 1,
      ...item,
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(allSeriesData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Serii");
  
    const fileName = `serii_${client.name.replace(/\s+/g, "_")}.xlsx`;
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  
    const data = new Blob([excelBuffer], { type: "application/octet-stream" });
    saveAs(data, fileName);
  };  

  const handleAddPanelSeries = () => {
    if (newPanel.trim() === "") {
      alert("Introduceți o serie validă!");
      return;
    }

    // ⏳ Verificăm dacă au trecut 5 secunde de la ultima adăugare
    const currentTime = Date.now();
    if (currentTime - lastPanelAddTime < 5000) { // 5000 ms = 5 secunde
      alert("Asteapta 5 secunde inainte de a adauga o noua serie!");
      return;
    }

    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_panel_series",
        client_id: id,
        series: newPanel,
      })
      .then(() => {
        setPanelSeries([{ id: Date.now(), series: newPanel }, ...panelSeries]);
        setNewPanel("");
        setLastPanelAddTime(currentTime);
      })
      .catch(() => alert("Eroare la adăugarea seriei."));
  };

  const handleDeletePanelSeries = (panelId) => {
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "delete_panel_series",
        id: panelId,
      })
      .then(() => setPanelSeries(panelSeries.filter(panel => panel.id !== panelId)))
      .catch(() => alert("Eroare la ștergerea seriei"));
  };

  const handleAddAcumulatorSeries = () => {
    if (newAcumulator.trim() === "") {
      alert("Introduceți o serie validă!");
      return;
    }
  
    const currentTime = Date.now();
    if (currentTime - lastAcumulatorAddTime < 5000) { // 5 secunde
      alert("Așteptați 5 secunde înainte de a adăuga o nouă serie de acumulator!");
      return;
    }
  
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_acumulator_series",
        client_id: id,
        series_acumulator: newAcumulator,
      })
      .then(() => {
        setAcumulatorSeries([{ id: Date.now(), series: newAcumulator }, ...acumulatorSeries]);
        setNewAcumulator("");
        setLastAcumulatorAddTime(currentTime);
      })
      .catch(() => alert("Eroare la adăugarea seriei acumulatorului."));
  };  
  
  const handleDeleteAcumulatorSeries = (acumulatorId) => {
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "delete_acumulator_series",
        id: acumulatorId,
      })
      .then(() => setAcumulatorSeries(acumulatorSeries.filter(acumulator => acumulator.id !== acumulatorId)))
      .catch(() => alert("Eroare la ștergerea seriei acumulatorului"));
  };  

  const handleAddInvertorSeries = () => {
    if (newInvertor.trim() === "") {
      alert("Introduceți o serie validă!");
      return;
    }
  
    const currentTime = Date.now();
    if (currentTime - lastInvertorAddTime < 5000) { // 5 secunde restricție
      alert("Asteptati 5 secunde inainte de a adauga o noua serie invertor!");
      return;
    }
  
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_invertor_series",
        client_id: id,
        series_invertor: newInvertor,
      })
      .then(() => {
        setInvertorSeries([{ id: Date.now(), series_invertor: newInvertor }, ...invertorSeries]);
        setNewInvertor("");
        setLastInvertorAddTime(currentTime);
      })
      .catch(() => alert("Eroare la adăugarea seriei invertor."));
  };
  
  const handleDeleteInvertorSeries = (invertorId) => {
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "delete_invertor_series",
        id: invertorId,
      })
      .then(() => setInvertorSeries(invertorSeries.filter(invertor => invertor.id !== invertorId)))
      .catch(() => alert("Eroare la ștergerea seriei invertor."));
  };

  const handleAddSmartMeterSeries = () => {
    if (newSmartMeter.trim() === "") {
      alert("Introduceți o serie validă!");
      return;
    }
  
    const currentTime = Date.now();
    if (currentTime - lastSmartMeterAddTime < 5000) {
      alert("Asteptati 5 secunde inainte de a adauga o noua serie Smart Meter!");
      return;
    }
  
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_smart_meter_series",
        client_id: id,
        series_smart_meter: newSmartMeter,
      })
      .then(() => {
        setSmartMeterSeries([{ id: Date.now(), series_smart_meter: newSmartMeter }, ...smartMeterSeries]);
        setNewSmartMeter("");
        setLastSmartMeterAddTime(currentTime);
      })
      .catch(() => alert("Eroare la adaugarea seriei Smart Meter."));
  };

  const handleDeleteSmartMeterSeries = (smartMeterId) => {
    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "delete_smart_meter_series",
        id: smartMeterId,
      })
      .then(() => setSmartMeterSeries(smartMeterSeries.filter(smartMeter => smartMeter.id !== smartMeterId)))
      .catch(() => alert("Eroare la ștergerea seriei Smart Meter"));
  };  

  const handleAddProsumatorLog = () => {
    if (newProsumatorLog.trim() === "") {
      alert("Introduceți un mesaj valid!");
      return;
    }

    if (!user || !user.user_id) {
      console.error("❌ Eroare: user_id lipsă");
      return;
    }

    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_prosumator_log",
        client_id: id,
        message: newProsumatorLog,
        user_id: user.user_id,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(() => {
        setProsumatorLogs([{ message: newProsumatorLog, user_name: user.name, created_at: new Date().toISOString() }, ...prosumatorLogs]);
        setNewProsumatorLog("");
      })
      .catch(() => alert("Eroare la adăugarea mesajului"));
  };

  const handleAddGeneralLog = () => {
    if (newGeneralLog.trim() === "") {
      alert("Introduceți un mesaj valid!");
      return;
    }

    if (!user || !user.user_id) {
      console.error("❌ Eroare: user_id lipsă");
      return;
    }

    axios
      .post("https://casaverde.marenergy.ro/api.php", {
        action: "add_general_log",
        client_id: id,
        message: newGeneralLog,
        user_id: user.user_id,
      }, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then(() => {
        setGeneralLogs([{ message: newGeneralLog, user_name: user.name, created_at: new Date().toISOString() }, ...generalLogs]);
        setNewGeneralLog("");
      })
      .catch(() => alert("Eroare la adăugarea mesajului general"));
  };

  return (
    <div className="container mt-4">
      <div className="user-info">
        <span className="user-name">👤 {user?.name || "Utilizator"}</span>
      </div>
      <div className="logo-container">
        <Link to="/dashboard">
          <img src="/images/Logo.webp" alt="Logo" className="clientprofile-logo" />
        </Link>
      </div>
      <hr />

      <h1 className="text-center">Profilul Clientului</h1>
      <hr />
    
      <h3 className="text-center">{client?.name}</h3>
      <br />

      {/* 🔹 Meniu pentru tab-uri */}
      <div className="tabs-container">
        <button 
          className={`tab-button ${activeTab === "informatii" ? "active" : ""}`}
          onClick={() => setActiveTab("informatii")}
        >
          Informatii
        </button>
        <button 
          className={`tab-button ${activeTab === "documente" ? "active" : ""}`}
          onClick={() => setActiveTab("documente")}
        >
          Documente si Serii
        </button>
      </div>

      <div className="card p-4 shadow mt-4">
        {activeTab === 'informatii' && (
          <>
            <p><strong>Echipa:</strong> {client?.echipa}</p>
            <p><strong>Email:</strong> {client?.email}</p>
            <p><strong>Telefon:</strong> {client?.phone}</p>
            <p><strong>Adresă:</strong> {client?.address || "Nicio adresă disponibilă"}</p>
            <hr />
            <p><strong>Panouri (kW):</strong> {client?.kW_panouri}kW / {client?.nrpanouri} buc</p>
            <p><strong>Acumulator (kW):</strong> {client?.kW_stocare}kW</p>
            <p><strong>Invertor (kW):</strong> {client?.kW_invertor}kW</p>
            <p><strong>Marca:</strong> {client?.Marca}</p>
            <hr />
            <p>
              <strong>Bransament:</strong> <span style={{ color: "red", fontWeight: "bold" }}>{client?.bransament}</span>
            </p>
            <p>
              <strong>Impamantare:</strong> <span style={{ color: "red", fontWeight: "bold" }}>{client?.impamantare}</span>
            </p>
            <p>
              <strong>Tip Acoperis:</strong> <span style={{ color: "red", fontWeight: "bold" }}>{client?.acoperis}</span>
            </p>
            <hr />

            <div className="mt-4">
              <label><strong>Status Client:</strong></label>
              <select className="form-control" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
              <button className="btn btn-warning mt-2" onClick={handleChangeStatus}>Schimba Status</button>

              {(lastStatusUpdate !== "Nicio actualizare" && lastUpdatedBy !== "Necunoscut") && (
                <p className="text-muted mt-2">
                  <small>Ultima actualizare: {lastStatusUpdate} de catre {lastUpdatedBy}</small>
                </p>
              )}
            </div>
          </>
        )}

        {activeTab === "documente" && (
          <>
            <div className="mt-4">
              <h4>📂 Documente Beneficiar</h4>

              {/* Dropdown pentru selectarea tipului de document */}
              <select 
                className="form-control mb-2" 
                value={selectedDocumentType} 
                onChange={(e) => setSelectedDocumentType(e.target.value)}
              >
                <option value="priza_pamant">Poza Priza Pamant</option>
                {user.role === "1" || user.role === "2" ? (
                  <option value="certificat_racordare">Certificat Racordare</option>
                ) : null}
              </select>

              {/* Afișăm un mesaj în funcție de tipul de document selectat */}
              {selectedDocumentType === "priza_pamant" && (
                <p className="text-muted small-message">Incarcati o poza in format JPG, JPEG sau PNG</p>
              )}
              {selectedDocumentType === "certificat_racordare" && (
                <p className="text-muted small-message">Incarcati certificatul de racordare in format PDF</p>
              )}

              {/* Input pentru încărcarea fișierului */}
              <input type="file" onChange={handleFileChange} className="form-control" />

              {/* Buton de upload */}
              <button 
                className="btn btn-primary mt-2" 
                onClick={handleUpload} 
                disabled={isUploading || (selectedDocumentType === "certificat_racordare" && documents.some(doc => doc.document_type === "certificat_racordare"))}
              >
                {isUploading ? "Se incarca..." : "Incarca Document"}
              </button>
            </div>

            {/* Lista documentelor încărcate */}
            <h5 className="mt-4">📑 Documente incarcate:</h5>

            {Array.isArray(documents) && documents.length > 0 ? (
              <ul className="list-group uploaded-documents">
                {documents.map((doc) => (
                  <li key={doc.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <a 
                      href={`https://casaverde.marenergy.ro${doc.file_path}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      title={doc.file_name} 
                      className="file-link"
                    >
                      {doc.file_name.length > 20 ? doc.file_name.substring(0, 20) + "..." : doc.file_name}
                    </a>
                    <span className="uploaded-by">
                      De: <strong>{doc.uploaded_by || "Necunoscut"}</strong>
                    </span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-muted">Niciun document incarcat</p>
            )}

            <hr />
            <div className="mt-4">
              <h4>Serii Panouri Fotovoltaice</h4>

              {panelSeries.length > 0 ? (
                <>
                  <ul className="list-group">
                    {currentItems.map((panel) => (
                      <li key={panel.id} className="list-group-item d-flex justify-content-between align-items-center">
                        {panel.series}

                        {user.role === "1" && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDeletePanelSeries(panel.id)}>
                            Șterge
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>

                  {/* 🔹 Butoane de paginare DOAR pentru lista de serii */}
                  {totalPages > 1 && (
                    <nav className="mt-3 d-flex justify-content-center">
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>Înapoi</button>
                        </li>

                        {Array.from({ length: totalPages }, (_, i) => i + 1)
                          .filter(page => Math.abs(page - currentPage) <= 1)
                          .map((page) => (
                            <li key={page} className={`page-item ${currentPage === page ? "active" : ""}`}>
                              <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                            </li>
                          ))}

                        <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                          <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>Înainte</button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              ) : (
                <p>Nicio serie introdusă</p>
              )}

              {(selectedStatus === "1" || selectedStatus === "2") && (
                <div className="serii-input-group mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Introduceti seria panoului"
                    value={newPanel}
                    onChange={(e) => setNewPanel(e.target.value)}
                  />
                  <button className="btn btn-primary" onClick={handleAddPanelSeries}>
                    Adauga Serie
                  </button>
                </div>
              )}
            </div>

            <hr />

            <div className="mt-4">
              <h4>Serii Acumulatori</h4>
              {acumulatorSeries.length > 0 ? (
                <ul className="list-group">
                  {acumulatorSeries.map((acumulator) => (
                    <li key={acumulator.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {acumulator.series}
                      {user.role === "1" && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteAcumulatorSeries(acumulator.id)}>
                          Șterge
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nicio serie de acumulator introdusa</p>
              )}

              {(selectedStatus === "1" || selectedStatus === "2") && (
                <div className="serii-input-group mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Introduceti seria acumulatorului"
                    value={newAcumulator}
                    onChange={(e) => setNewAcumulator(e.target.value)}
                  />
                  <button className="btn btn-primary" onClick={handleAddAcumulatorSeries}>
                    Adauga Serie
                  </button>
                </div>
              )}  
            </div>

            <hr />

            <div className="mt-4">
              <h4>Serii Invertoare</h4>
              {invertorSeries.length > 0 ? (
                <ul className="list-group">
                  {invertorSeries.map((invertor) => (
                    <li key={invertor.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {invertor.series_invertor}
                      {user.role === "1" && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteInvertorSeries(invertor.id)}>
                          Șterge
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nicio serie de invertor introdusa</p>
              )}

              {(selectedStatus === "1" || selectedStatus === "2") && (
                <div className="serii-input-group mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Introduceti seria invertorului"
                    value={newInvertor}
                    onChange={(e) => setNewInvertor(e.target.value)}
                  />
                  <button className="btn btn-primary" onClick={handleAddInvertorSeries}>
                    Adauga Serie
                  </button>
                </div>
              )}
            </div>

            <hr />

            <div className="mt-4">
              <h4>Serii Smart Meter</h4>
              {smartMeterSeries.length > 0 ? (
                <ul className="list-group">
                  {smartMeterSeries.map((smartMeter) => (
                    <li key={smartMeter.id} className="list-group-item d-flex justify-content-between align-items-center">
                      {smartMeter.series_smart_meter}
                      {user.role === "1" && (
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteSmartMeterSeries(smartMeter.id)}>
                          Șterge
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p>Nicio serie de Smart Meter introdusa</p>
              )}

              {(selectedStatus === "1" || selectedStatus === "2") && (
                <div className="serii-input-group mt-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Introduceți seria Smart Meter"
                    value={newSmartMeter}
                    onChange={(e) => setNewSmartMeter(e.target.value)}
                  />
                  <button className="btn btn-primary" onClick={handleAddSmartMeterSeries}>
                    Adauga Serie
                  </button>
                </div>
              )}

              {/* Buton pentru descărcare Excel */}
              {panelSeries.length > 0 && (
                <button className="btn btn-success mt-3" onClick={handleDownloadExcel}>
                  📥 Descarca Serii (Excel)
                </button>
              )} 

            </div>
          </>
        )}

        <hr />
        {(user.role === "1" || user.role === "2") && (
          <button className="btn btn-info mt-3" onClick={() => setShowProsumatorModal(true)}>
            Dosar Prosumator
          </button>
        )}
        <button className="btn btn-secondary mt-3 ml-2" onClick={() => setShowGeneralLogsModal(true)}>Mesaje Generale</button>

        <Link to="/clients" className="btn btn-primary mt-3">Înapoi la Clienți</Link>
        <hr />
      </div>

      {showProsumatorModal && (
        <Modal title="Istoric Dosar Prosumator" logs={prosumatorLogs} newLog={newProsumatorLog} setNewLog={setNewProsumatorLog} handleAddLog={handleAddProsumatorLog} closeModal={() => setShowProsumatorModal(false)} />
      )}
      {showGeneralLogsModal && (
        <Modal title="Mesaje Generale" logs={generalLogs} newLog={newGeneralLog} setNewLog={setNewGeneralLog} handleAddLog={handleAddGeneralLog} closeModal={() => setShowGeneralLogsModal(false)} />
      )}
    </div>
  );
};

const Modal = ({ title, logs, newLog, setNewLog, handleAddLog, closeModal }) => (
  <div className="modal show d-block" style={{ background: "rgba(0, 0, 0, 0.5)" }}>
    <div className="modal-dialog">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">{title}</h5>
          <button className="close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <ul className="list-group">
            {logs.length > 0 ? (
              logs.map((log, index) => (
                <li key={index} className="list-group-item">
                  <strong>{new Date(log.created_at).toLocaleString()}</strong> -
                  <span style={{ color: "blue", fontWeight: "bold" }}>{log.user_name || "Necunoscut"}:</span> {log.message}
                </li>
              ))
            ) : (
              <p>Niciun mesaj adăugat</p>
            )}
          </ul>
          <textarea className="form-control mt-3" rows="3" placeholder="Adaugă un mesaj..." value={newLog} onChange={(e) => setNewLog(e.target.value)}></textarea>
          <button className="btn btn-primary mt-2" onClick={handleAddLog}>Adaugă Mesaj</button>
        </div>
      </div>
    </div>
  </div>
);

export default ClientProfile;